import { useEffect, useState } from 'react'
import { Bell, BellOff, BellRing, X } from 'lucide-react'
import { pushState, enablePush, disablePush, sendTestPush } from '../lib/push.js'
import { toast } from '../components/feedback/Toast.jsx'

/**
 * The two faces of push opt-in (Charles, 2026-09-11):
 *   NotificationsCard — the full control on the More page: state, on/off,
 *     a test send, and what to do when the phone says no.
 *   PushNudge — one line on the Session Log home, shown only while this
 *     device could opt in and hasn't; engineers live in the Session Log
 *     and never see More, so this is where they'd actually find it.
 * Both read the same state: what THIS browser holds, not the server.
 */

function usePushState() {
  const [state, setState] = useState('checking')
  useEffect(() => {
    let live = true
    pushState().then((s) => live && setState(s))
    return () => { live = false }
  }, [])
  return [state, setState]
}

const INSTALL_HELP = 'Add the app to your Home Screen first (Share → Add to Home Screen), then turn this on from there.'

async function turnOn(setState, setBusy) {
  setBusy(true)
  try {
    const next = await enablePush()
    setState(next)
    if (next === 'on') toast('Notifications on — a test just went to this device')
    else if (next === 'denied') toast('Notifications were blocked — allow them in your phone’s Settings', 'error')
  } catch (err) {
    toast(err?.message || 'Couldn’t turn on notifications', 'error')
  } finally {
    setBusy(false)
  }
}

export function NotificationsCard() {
  const [state, setState] = usePushState()
  const [busy, setBusy] = useState(false)

  async function turnOff() {
    setBusy(true)
    try {
      setState(await disablePush())
      toast('Notifications off on this device')
    } finally {
      setBusy(false)
    }
  }

  async function test() {
    setBusy(true)
    try {
      const r = await sendTestPush()
      toast(r.ok ? 'Test sent — check your lock screen' : 'No device answered — try turning notifications off and on', r.ok ? 'info' : 'error')
    } catch {
      toast('Couldn’t send the test', 'error')
    } finally {
      setBusy(false)
    }
  }

  const line = {
    checking: 'Checking…',
    on: 'On for this device — a missed session log pings you here at 6 PM instead of by email.',
    off: 'Off — the reminder arrives by email. Turn on to get it on this device’s lock screen instead.',
    denied: 'Blocked by this phone. Allow notifications for CDM Studios in Settings, then come back.',
    install: INSTALL_HELP,
    unsupported: 'This browser can’t receive notifications. The reminder arrives by email.',
  }[state]

  return (
    <section>
      <h2 className="section-header mb-2">Notifications</h2>
      <div className="card px-4 py-3">
        <div className="flex items-start gap-3">
          {state === 'on' ? <BellRing size={17} className="mt-0.5 shrink-0 text-navy" />
            : state === 'denied' ? <BellOff size={17} className="mt-0.5 shrink-0 text-muted" />
            : <Bell size={17} className="mt-0.5 shrink-0 text-muted" />}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-ink">Session log reminders</div>
            <p className="mt-0.5 text-xs leading-relaxed text-muted">{line}</p>
          </div>
        </div>
        {(state === 'off' || state === 'on') && (
          <div className="mt-3 flex gap-2">
            {state === 'off' ? (
              <button
                onClick={() => turnOn(setState, setBusy)}
                disabled={busy}
                className="tap rounded-xl bg-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                Turn on
              </button>
            ) : (
              <>
                <button
                  onClick={test}
                  disabled={busy}
                  className="tap rounded-xl border-ink px-4 py-2 text-sm font-medium text-ink disabled:opacity-60"
                  style={{ borderWidth: '1.5px' }}
                >
                  Send a test
                </button>
                <button
                  onClick={turnOff}
                  disabled={busy}
                  className="tap rounded-xl px-4 py-2 text-sm font-medium text-danger disabled:opacity-60"
                >
                  Turn off
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

const NUDGE_KEY = 'push-nudge-hidden'
const nudgeHidden = () => {
  try { return localStorage.getItem(NUDGE_KEY) === '1' } catch { return false }
}

export function PushNudge() {
  const [state, setState] = usePushState()
  const [busy, setBusy] = useState(false)
  const [hidden, setHidden] = useState(nudgeHidden)
  // Only a device that could opt in and hasn't: on, denied and unsupported
  // have nothing to offer here (the More page explains those).
  if (hidden || !(state === 'off' || state === 'install')) return null

  const dismiss = () => {
    setHidden(true)
    try { localStorage.setItem(NUDGE_KEY, '1') } catch { /* private mode */ }
  }

  return (
    <div className="card mb-5 flex items-center gap-3 px-4 py-3">
      <Bell size={17} className="shrink-0 text-navy" />
      <button
        onClick={() => (state === 'install' ? toast(INSTALL_HELP) : turnOn(setState, setBusy))}
        disabled={busy}
        className="tap min-w-0 flex-1 text-left disabled:opacity-60"
      >
        <div className="text-sm font-medium text-ink">
          {state === 'install' ? 'Get reminders on this phone' : 'Turn on reminders for this phone'}
        </div>
        <div className="mt-0.5 text-xs text-muted">
          {state === 'install'
            ? 'Add the app to your Home Screen, then tap here'
            : 'A lock-screen ping at 6 PM if a session log looks missed'}
        </div>
      </button>
      <button onClick={dismiss} aria-label="Hide" className="tap -mr-1 shrink-0 p-1 text-faint">
        <X size={15} />
      </button>
    </div>
  )
}
